import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FilmStripLoader from "../components/FilmStripLoader";
import AnimateReveal from "../components/AnimateReveal";
import { getYouTubeEmbedUrl } from "../utils/youtubeEmbed";

import trial from "../../src/assets/images/trial-bg.png";

/** Match canonical French genre stored in DB (case-insensitive). */
function movieHasGenre(m, frenchLabel) {
    const want = String(frenchLabel).trim().toLowerCase();
    return m.genre?.some((g) => String(g).trim().toLowerCase() === want);
}

/** Newest first for hero / Tendances / Nouveautés (updatedAt helps older imports). */
function catalogRecencyTime(doc) {
    const raw = doc?.createdAt || doc?.updatedAt;
    if (!raw) return 0;
    const n = new Date(raw).getTime();
    return Number.isNaN(n) ? 0 : n;
}

// Smooth carousel scroll: disable snap during scroll to avoid glitches, then animate with easing
const CAROUSEL_SMOOTH_CLASS = "carousel-smooth-scrolling";
const CAROUSEL_DURATION_MS = 700;
const activeAnimations = new WeakMap(); // Track ongoing animations per element

const smoothScrollCarousel = (element, direction, amountRatio = 0.5) => {
    if (!element) return;

    // Cancel any ongoing animation for this element
    const existingAnimation = activeAnimations.get(element);
    if (existingAnimation) {
        cancelAnimationFrame(existingAnimation);
    }

    const start = element.scrollLeft;
    const maxScroll = element.scrollWidth - element.clientWidth;

    // Check if we're at the bounds - don't animate if we can't scroll further
    if (direction === "left" && start <= 0) return;
    if (direction === "right" && start >= maxScroll) return;

    element.classList.add(CAROUSEL_SMOOTH_CLASS);
    const distance = direction === "left" ? -(element.clientWidth * amountRatio) : element.clientWidth * amountRatio;
    const target = Math.max(0, Math.min(start + distance, maxScroll));
    const startTime = performance.now();
    // Ease-in-out for smooth start and end
    const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    const step = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / CAROUSEL_DURATION_MS, 1);
        const eased = easeInOutCubic(progress);
        element.scrollLeft = start + (target - start) * eased;
        if (progress < 1) {
            const rafId = requestAnimationFrame(step);
            activeAnimations.set(element, rafId);
        } else {
            element.classList.remove(CAROUSEL_SMOOTH_CLASS);
            activeAnimations.delete(element);
        }
    };
    const rafId = requestAnimationFrame(step);
    activeAnimations.set(element, rafId);
};

const Home = () => {
    const [movies, setMovies] = useState([]);
    const [trendingMovies, setTrendingMovies] = useState([]);
    const [trendingSeries, setTrendingSeries] = useState([]);
    const [newReleaseMovies, setNewReleaseMovies] = useState([]);
    const [newReleaseSeries, setNewReleaseSeries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showLoader, setShowLoader] = useState(true);
    const [trailerUrl, setTrailerUrl] = useState(null);
    const [showTrailer, setShowTrailer] = useState(false);
    const [currentTrendingIndex, setCurrentTrendingIndex] = useState(0);

    useEffect(() => {
        const loadAllData = async () => {
            setLoading(true);
            try {
                // Wait for all API calls to complete before hiding loader
                await Promise.all([fetchMovies(), fetchTrendingMovies(), fetchTrendingSeries(), fetchNewReleaseMovies(), fetchNewReleaseSeries()]);
            } catch (error) {
                console.error("Error loading data:", error);
            } finally {
                // Small delay to ensure everything is rendered before hiding loader
                setTimeout(() => {
                    setLoading(false);
                    // Start fade-out animation, then hide loader after animation completes
                    setTimeout(() => {
                        setShowLoader(false);
                    }, 600);
                }, 500);
            }
        };

        loadAllData();
    }, []);

    const fetchMovies = async () => {
        try {
            const res = await axios.get("/api/movies");
            setMovies(res.data);
        } catch (error) {
            console.error("Error fetching movies:", error);
        }
    };

    const fetchTrendingMovies = async () => {
        try {
            const res = await axios.get("/api/movies/trending");
            setTrendingMovies(res.data);
        } catch (error) {
            console.error("Error fetching trending movies:", error);
        }
    };

    const fetchTrendingSeries = async () => {
        try {
            const res = await axios.get("/api/series/trending");
            setTrendingSeries(res.data);
        } catch (error) {
            console.error("Error fetching trending series:", error);
        }
    };

    const fetchNewReleaseMovies = async () => {
        try {
            const res = await axios.get("/api/movies/new-releases");
            setNewReleaseMovies(res.data);
        } catch (error) {
            console.error("Error fetching new release movies:", error);
        }
    };

    const fetchNewReleaseSeries = async () => {
        try {
            const res = await axios.get("/api/series/new-releases");
            setNewReleaseSeries(res.data);
        } catch (error) {
            console.error("Error fetching new release series:", error);
        }
    };

    /** Hero: trending films + séries (API), newest first — includes anime flagged as trending. */
    const trendingHeroItems = useMemo(() => {
        const rows = [
            ...trendingMovies.map((item) => ({ kind: "movie", item, t: catalogRecencyTime(item) })),
            ...trendingSeries.map((item) => ({ kind: "series", item, t: catalogRecencyTime(item) })),
        ];
        rows.sort((a, b) => b.t - a.t);
        return rows.slice(0, 20).map(({ kind, item }) => ({ kind, item }));
    }, [trendingMovies, trendingSeries]);

    useEffect(() => {
        if (trendingHeroItems.length > 1) {
            const interval = setInterval(() => {
                setCurrentTrendingIndex((prev) => (prev + 1) % trendingHeroItems.length);
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [trendingHeroItems.length]);

    useEffect(() => {
        setCurrentTrendingIndex((i) => {
            if (!trendingHeroItems.length) return 0;
            return i >= trendingHeroItems.length ? 0 : i;
        });
    }, [trendingHeroItems.length]);

    const nextTrending = () => {
        if (!trendingHeroItems.length) return;
        setCurrentTrendingIndex((prev) => (prev + 1) % trendingHeroItems.length);
    };

    const prevTrending = () => {
        if (!trendingHeroItems.length) return;
        setCurrentTrendingIndex((prev) => (prev - 1 + trendingHeroItems.length) % trendingHeroItems.length);
    };

    /** Movies + series interleaved by most recently added (for Nouveautés) */
    const newReleasesMerged = useMemo(() => {
        const rows = [
            ...newReleaseMovies.map((item) => ({
                kind: "movie",
                item,
                t: catalogRecencyTime(item),
            })),
            ...newReleaseSeries.map((item) => ({
                kind: "series",
                item,
                t: catalogRecencyTime(item),
            })),
        ];
        rows.sort((a, b) => b.t - a.t);
        return rows;
    }, [newReleaseMovies, newReleaseSeries]);

    /** Tendances: same sources as hero (trending API), not the full movies list */
    const trendingMerged = useMemo(() => {
        const rows = [
            ...trendingMovies.map((item) => ({
                kind: "movie",
                item,
                t: catalogRecencyTime(item),
            })),
            ...trendingSeries.map((item) => ({
                kind: "series",
                item,
                t: catalogRecencyTime(item),
            })),
        ];
        rows.sort((a, b) => b.t - a.t);
        return rows;
    }, [trendingMovies, trendingSeries]);

    if (showLoader) {
        return (
            <div className={loading ? "" : "fade-out-loader"}>
                <FilmStripLoader />
            </div>
        );
    }

    const genres = [
        { key: "action", title: "Action", genreParam: "Action", movies: movies.filter((m) => movieHasGenre(m, "Action")).slice(0, 4) },
        { key: "aventure", title: "Aventure", genreParam: "Aventure", movies: movies.filter((m) => movieHasGenre(m, "Aventure")).slice(0, 4) },
        { key: "drame", title: "Drame", genreParam: "Drame", movies: movies.filter((m) => movieHasGenre(m, "Drame")).slice(0, 4) },
        { key: "comedie", title: "Comédie", genreParam: "Comédie", movies: movies.filter((m) => movieHasGenre(m, "Comédie")).slice(0, 4) },
        { key: "horreur", title: "Horreur", genreParam: "Horreur", movies: movies.filter((m) => movieHasGenre(m, "Horreur")).slice(0, 4) },
        {
            key: "science-fiction",
            title: "Science-fiction",
            genreParam: "Science-fiction",
            movies: movies.filter((m) => movieHasGenre(m, "Science-fiction")).slice(0, 4),
        },
    ];

    const formatDuration = (minutes) => {
        if (!minutes) return "—";
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return h > 0 ? `${h}h ${m}min` : `${m}min`;
    };

    const formatSeasons = (count) => {
        const n = count || 0;
        return n === 1 ? "1 Season" : `${n} Seasons`;
    };

    return (
        <div className="min-h-screen bg-[#141414] flex flex-col">
            <Header />

            <div className="flex-1 p-4 sm:p-6 md:p-10 rounded-2xl overflow-x-hidden">
                {trendingHeroItems.length > 0 && (
                    <AnimateReveal animation="fadeUp">
                        <div className="relative h-[70vh] mb-12">
                            {trendingHeroItems.map(({ kind, item }, index) => {
                                const is_attachment = index === currentTrendingIndex;
                                const href = kind === "movie" ? `/movie/${item._id}` : `/series/${item._id}`;
                                const ctaLabel = kind === "movie" ? "Lire maintenant" : "Découvrir la série";

                                return (
                                    <div key={`${kind}-${item._id}`} className={`absolute inset-0 transition-opacity duration-500 ${is_attachment ? "opacity-100 z-10" : "opacity-0 z-0"}`}>
                                        <div className="absolute inset-0 rounded-3xl overflow-hidden bg-[#141414]">
                                            <div
                                                className="absolute inset-0 bg-center bg-no-repeat"
                                                style={{
                                                    backgroundImage: `url(${item.poster})`,
                                                    backgroundSize: "cover",
                                                    backgroundPosition: "center",
                                                    backgroundRepeat: "no-repeat",
                                                }}
                                            />

                                            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/10 to-black/70" />

                                            <div
                                                className="absolute inset-0"
                                                style={{
                                                    background: "linear-gradient(to top, #141414 0%, #141414cc 40%, #14141466 65%, #14141400 100%)",
                                                }}
                                            />
                                        </div>

                                        <div className="relative container mx-auto px-4 h-full flex items-end justify-center pb-24">
                                            <div className="max-w-3xl text-center">
                                                {kind === "series" && (
                                                    <p className="text-sm font-semibold uppercase tracking-wider text-white/70 mb-2">Série</p>
                                                )}
                                                <h1 className="text-5xl font-bold text-white mb-4">{item.title}</h1>
                                                <p className="text-gray-300 mb-6 line-clamp-3">{item.description}</p>

                                                <div className="flex items-center justify-center gap-3 flex-wrap">
                                                    <Link
                                                        to={href}
                                                        className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition font-semibold"
                                                    >
                                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                                        </svg>
                                                        <span>{ctaLabel}</span>
                                                    </Link>

                                                    {item.trailer && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setTrailerUrl(item.trailer);
                                                                setShowTrailer(true);
                                                            }}
                                                            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900/60 text-white rounded-lg hover:bg-gray-900/80 transition font-semibold"
                                                        >
                                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                                            </svg>
                                                            <span>Voir la bande-annonce</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            {trendingHeroItems.length > 1 && (
                                <div className="absolute inset-x-0 bottom-4 sm:bottom-6 z-30 flex flex-col items-center gap-3 sm:gap-4 pointer-events-none">
                                    <div className="flex w-full items-center justify-between px-4 sm:px-6 pointer-events-auto">
                                        <button
                                            type="button"
                                            onClick={prevTrending}
                                            className="w-12 h-12 shrink-0 rounded-lg bg-black/45 hover:bg-black/60 transition flex items-center justify-center text-white border border-[#1F1F1F]"
                                            aria-label="Previous"
                                        >
                                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M22.75 14L5.25 14M5.25 14L13.125 21.875M5.25 14L13.125 6.125" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={nextTrending}
                                            className="w-12 h-12 shrink-0 rounded-lg bg-black/45 hover:bg-black/60 transition flex items-center justify-center text-white border border-[#1F1F1F]"
                                            aria-label="Next"
                                        >
                                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M15.75 5.25L24.5 14M24.5 14L15.75 22.75M24.5 14H3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </div>

                                    <div className="pointer-events-auto flex max-w-[min(100%,20rem)] sm:max-w-md justify-center items-center gap-2 overflow-x-auto px-2 py-0.5">
                                        {trendingHeroItems.map((_, idx) => (
                                            <button
                                                type="button"
                                                key={idx}
                                                onClick={() => setCurrentTrendingIndex(idx)}
                                                className={`h-1.5 shrink-0 rounded-full transition ${idx === currentTrendingIndex ? "bg-white w-10" : "bg-white/40 w-5"}`}
                                                aria-label={`Go to slide ${idx + 1}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </AnimateReveal>
                )}
                <div className="flex-1 max-sm:px-2 px-6 md:px-10 py-10">
                    {/* OUTER BOX like screenshot */}
                    <div className="relative rounded-3xl border border-[#262626] overflow-visible">
                        {/* soft glow / gradient background (optional but nice) */}
                        <div className="pointer-events-none absolute inset-0" />
                        <div className="pointer-events-none absolute -top-20 left-1/2 h-40 w-[700px] -translate-x-1/2 rounded-full " />
                        <div className="absolute -top-4 left-8 z-20">
                            <div className="px-4 py-2 rounded-lg bg-white text-black text-sm font-semibold shadow">Films</div>
                        </div>
                        <div className="relative p-4 sm:p-6 md:p-10">
                            {/* ===================== OUR GENRES ===================== */}
                            <AnimateReveal animation="fadeUp" delay={50}>
                                <div className="container mx-auto px-2 sm:px-4 md:px-0 mb-20 ">
                                    <div className="flex items-center justify-between mb-6 gap-3">
                                        <h2 className="text-2xl md:text-3xl font-bold text-white">Nos genres</h2>

                                        {/* Controls (responsive) */}
                                        <div className="hidden sm:flex items-center rounded-2xl bg-[#0F0F0F] border border-white/10 px-4 md:px-5 py-3 md:py-3.5">
                                            <div className="flex items-center justify-center gap-4">
                                                <button
                                                    type="button"
                                                    className="w-9 h-9 md:w-10 md:h-10 shrink-0 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition flex items-center justify-center"
                                                    aria-label="Défiler vers la gauche"
                                                    onClick={() => {
                                                        const el = document.getElementById("genres-scroll");
                                                        smoothScrollCarousel(el, "left");
                                                    }}
                                                >
                                                    <svg width="20" height="20" viewBox="0 0 28 28" fill="none">
                                                        <path
                                                            d="M22.75 14L5.25 14M5.25 14L13.125 21.875M5.25 14L13.125 6.125"
                                                            stroke="white"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </button>

                                                <button
                                                    type="button"
                                                    className="w-9 h-9 md:w-10 md:h-10 shrink-0 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition flex items-center justify-center"
                                                    aria-label="Défiler vers la droite"
                                                    onClick={() => {
                                                        const el = document.getElementById("genres-scroll");
                                                        smoothScrollCarousel(el, "right");
                                                    }}
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 28 28" fill="none">
                                                        <path d="M15.75 5.25L24.5 14M24.5 14L15.75 22.75M24.5 14H3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="relative md:overflow-hidden">
                                            <div
                                            id="genres-scroll"
                                            className="
                      flex gap-3 sm:gap-4 md:gap-5
                      overflow-x-auto scroll-smooth
                      snap-x snap-mandatory
                      pb-4 min-w-0
                      pl-2 pr-4 sm:pl-4 sm:pr-6
                    "
                                        >
                                            {genres.map((genre) => (
                                                <Link
                                                    key={genre.key}
                                                    to={`/movies?genre=${encodeURIComponent(genre.genreParam)}`}
                                                    className="
                          group relative flex-shrink-0 snap-start
                          w-[175px] sm:w-[195px] md:w-[215px] lg:w-[235px]
                          rounded-2xl bg-[#1A1A1A] p-4 overflow-hidden
                        "
                                                >
                                                    <div className="grid grid-cols-2 gap-3 mb-5">
                                                        {genre.movies.map((movie) => (
                                                            <div key={movie._id} className="relative aspect-[2/3] rounded-lg overflow-hidden">
                                                                <img
                                                                    src={movie.poster}
                                                                    alt={movie.title}
                                                                    className="w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />

                                                    <div className="relative flex items-center justify-between">
                                                        <h3 className="font-semibold text-white">{genre.title}</h3>

                                                        <svg width="26" height="26" viewBox="0 0 30 30" fill="none" className="opacity-90 group-hover:opacity-100 transition">
                                                            <path
                                                                d="M5.625 15L24.375 15M24.375 15L15.9375 6.5625M24.375 15L15.9375 23.4375"
                                                                stroke="white"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </AnimateReveal>

                            {/* ===================== TRENDING NOW ===================== */}
                            {trendingMerged.length > 0 && (
                                <AnimateReveal animation="fadeUp" delay={100}>
                                    <div className="container mx-auto px-2 sm:px-4 md:px-0 mb-16">
                                        <div className="flex items-center justify-between mb-6 gap-3">
                                            <h2 className="text-2xl md:text-3xl font-bold text-white">Tendances</h2>

                                            <div className="hidden sm:flex items-center rounded-2xl bg-[#0F0F0F] border border-white/10 px-4 md:px-5 py-3 md:py-3.5">
                                                <div className="flex items-center justify-center gap-4">
                                                    <button
                                                        type="button"
                                                        className="w-9 h-9 md:w-10 md:h-10 shrink-0 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition flex items-center justify-center"
                                                        aria-label="Défiler vers la gauche"
                                                        onClick={() => {
                                                            const el = document.getElementById("trending-scroll");
                                                            smoothScrollCarousel(el, "left");
                                                        }}
                                                    >
                                                        <svg width="20" height="20" viewBox="0 0 28 28" fill="none">
                                                            <path
                                                                d="M22.75 14L5.25 14M5.25 14L13.125 21.875M5.25 14L13.125 6.125"
                                                                stroke="white"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="w-9 h-9 md:w-10 md:h-10 shrink-0 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition flex items-center justify-center"
                                                        aria-label="Défiler vers la droite"
                                                        onClick={() => {
                                                            const el = document.getElementById("trending-scroll");
                                                            smoothScrollCarousel(el, "right");
                                                        }}
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 28 28" fill="none">
                                                            <path d="M15.75 5.25L24.5 14M24.5 14L15.75 22.75M24.5 14H3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="relative md:overflow-hidden">
                                            <div id="trending-scroll" className="flex gap-3 sm:gap-4 md:gap-5 overflow-x-auto pb-4 min-w-0 snap-x snap-mandatory scroll-smooth -mx-2 sm:mx-0">
                                                {trendingMerged.map(({ kind, item }) =>
                                                    kind === "movie" ? (
                                                        <Link key={`m-${item._id}`} to={`/movie/${item._id}`} className="group flex-shrink-0 snap-start w-full sm:w-[220px] md:w-[240px] lg:w-[260px]">
                                                            <div className="rounded-3xl bg-[#1A1A1A] border border-white/5 p-4 transition">
                                                                <div className="relative aspect-[2/3] rounded-2xl overflow-hidden bg-black/40">
                                                                    <img
                                                                        src={item.poster}
                                                                        alt={item.title}
                                                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
                                                                    />
                                                                </div>

                                                                <h3 className="mt-4 text-sm font-semibold text-white line-clamp-1">{item.title}</h3>

                                                                <div className="mt-4 flex items-center justify-between">
                                                                    <span className="px-3 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/70">{formatDuration(item.duration)}</span>

                                                                    <svg width="26" height="26" viewBox="0 0 30 30" fill="none">
                                                                        <path
                                                                            d="M5.625 15L24.375 15M24.375 15L15.9375 6.5625M24.375 15L15.9375 23.4375"
                                                                            stroke="white"
                                                                            strokeWidth="2"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                        </Link>
                                                    ) : (
                                                        <Link key={`s-${item._id}`} to={`/series/${item._id}`} className="group flex-shrink-0 snap-start w-full sm:w-[220px] md:w-[240px] lg:w-[260px]">
                                                            <div className="rounded-3xl bg-[#1A1A1A] border border-white/5 p-4 transition">
                                                                <div className="relative aspect-[2/3] rounded-2xl overflow-hidden bg-black/40">
                                                                    <img
                                                                        src={item.poster}
                                                                        alt={item.title}
                                                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
                                                                    />
                                                                </div>

                                                                <h3 className="mt-4 text-sm font-semibold text-white line-clamp-1">{item.title}</h3>

                                                                <div className="mt-4 flex items-center justify-between">
                                                                    <span className="px-3 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/70">
                                                                        {formatSeasons(item.seasons?.length)}
                                                                    </span>

                                                                    <svg width="26" height="26" viewBox="0 0 30 30" fill="none">
                                                                        <path
                                                                            d="M5.625 15L24.375 15M24.375 15L15.9375 6.5625M24.375 15L15.9375 23.4375"
                                                                            stroke="white"
                                                                            strokeWidth="2"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                        </Link>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </AnimateReveal>
                            )}

                            {/* ===================== NEW RELEASES ===================== */}
                            {newReleasesMerged.length > 0 && (
                                <AnimateReveal animation="fadeUp" delay={150}>
                                    <div className="container mx-auto px-2 sm:px-4 md:px-0 mb-20">
                                        <div className="flex items-center justify-between mb-6 gap-3">
                                            <h2 className="text-2xl md:text-3xl font-bold text-white">Nouveaut&eacute;s</h2>

                                            <div className="hidden sm:flex items-center rounded-2xl bg-[#0F0F0F] border border-white/10 px-4 md:px-5 py-3 md:py-3.5">
                                                <div className="flex items-center justify-center gap-4">
                                                    <button
                                                        type="button"
                                                        className="w-9 h-9 md:w-10 md:h-10 shrink-0 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition flex items-center justify-center"
                                                        aria-label="Défiler vers la gauche"
                                                        onClick={() => {
                                                            const el = document.getElementById("new-releases-scroll");
                                                            smoothScrollCarousel(el, "left");
                                                        }}
                                                    >
                                                        <svg width="20" height="20" viewBox="0 0 28 28" fill="none">
                                                            <path
                                                                d="M22.75 14L5.25 14M5.25 14L13.125 21.875M5.25 14L13.125 6.125"
                                                                stroke="white"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="w-9 h-9 md:w-10 md:h-10 shrink-0 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition flex items-center justify-center"
                                                        aria-label="Défiler vers la droite"
                                                        onClick={() => {
                                                            const el = document.getElementById("new-releases-scroll");
                                                            smoothScrollCarousel(el, "right");
                                                        }}
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 28 28" fill="none">
                                                            <path d="M15.75 5.25L24.5 14M24.5 14L15.75 22.75M24.5 14H3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="relative md:overflow-hidden">
                                            <div id="new-releases-scroll" className="flex gap-3 sm:gap-4 md:gap-5 overflow-x-auto pb-4 min-w-0 snap-x snap-mandatory scroll-smooth -mx-2 sm:mx-0">
                                                {newReleasesMerged.map(({ kind, item }) =>
                                                    kind === "movie" ? (
                                                        <Link key={`nr-m-${item._id}`} to={`/movie/${item._id}`} className="group flex-shrink-0 snap-start w-full sm:w-[220px] md:w-[240px] lg:w-[260px]">
                                                            <div className="rounded-3xl bg-[#1A1A1A] border border-white/5 p-4 transition">
                                                                <div className="relative aspect-[2/3] rounded-2xl overflow-hidden bg-black/40">
                                                                    <img
                                                                        src={item.poster}
                                                                        alt={item.title}
                                                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
                                                                    />
                                                                </div>

                                                                <h3 className="mt-4 text-sm font-semibold text-white line-clamp-1">{item.title}</h3>

                                                                <div className="mt-4 flex items-center justify-between">
                                                                    <span className="px-3 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/70">{formatDuration(item.duration)}</span>

                                                                    <svg width="26" height="26" viewBox="0 0 30 30" fill="none">
                                                                        <path
                                                                            d="M5.625 15L24.375 15M24.375 15L15.9375 6.5625M24.375 15L15.9375 23.4375"
                                                                            stroke="white"
                                                                            strokeWidth="2"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                        </Link>
                                                    ) : (
                                                        <Link key={`nr-s-${item._id}`} to={`/series/${item._id}`} className="group flex-shrink-0 snap-start w-full sm:w-[220px] md:w-[240px] lg:w-[260px]">
                                                            <div className="rounded-3xl bg-[#1A1A1A] border border-white/5 p-4 transition">
                                                                <div className="relative aspect-[2/3] rounded-2xl overflow-hidden bg-black/40">
                                                                    <img
                                                                        src={item.poster}
                                                                        alt={item.title}
                                                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
                                                                    />
                                                                </div>

                                                                <h3 className="mt-4 text-sm font-semibold text-white line-clamp-1">{item.title}</h3>

                                                                <div className="mt-4 flex items-center justify-between">
                                                                    <span className="px-3 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/70">
                                                                        {formatSeasons(item.seasons?.length)}
                                                                    </span>

                                                                    <svg width="26" height="26" viewBox="0 0 30 30" fill="none">
                                                                        <path
                                                                            d="M5.625 15L24.375 15M24.375 15L15.9375 6.5625M24.375 15L15.9375 23.4375"
                                                                            stroke="white"
                                                                            strokeWidth="2"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                        </Link>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </AnimateReveal>
                            )}
                        </div>
                    </div>
                </div>
                <div className="container mx-auto px-4 mb-12">
                    <div className="relative rounded-2xl overflow-hidden">
                        <img src={trial} alt="Free trial background" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 " />
                        <div className="relative z-10 p-8 md:p-20 ">
                            <div className="flex items-center justify-between flex-col md:flex-row">
                                <div className="flex flex-col items-start">
                                    <h2 className="text-center md:text-start text-3xl md:text-4xl font-semibold text-white mb-4">Des milliers de films et s&eacute;ries, d&eacute;s maintenant !</h2>

                                    <p className="text-gray-300 mb-6 text-center md:text-start">
                                        Acc&eacute;s illimit&eacute; au catalogue complet en Full HD 1080p, sans publicit&eacute;.
                                        <br />
                                        Compatible TV, PC, Mobile et Mac.
                                    </p>
                                </div>
                                <Link
                                    to="/subscriptions"
                                    className="inline-flex items-center justify-center px-6 py-3
                                bg-white text-gray-900 rounded-xl
                                hover:bg-gray-100 transition font-semibold"
                                >
                                    Acc&eacute;s imm&eacute;diat
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showTrailer && trailerUrl && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
                    onClick={() => {
                        setShowTrailer(false);
                        setTrailerUrl(null);
                    }}
                >
                    <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => {
                                setShowTrailer(false);
                                setTrailerUrl(null);
                            }}
                            className="absolute -top-10 right-0 text-white text-2xl hover:text-gray-300 transition"
                        >
                            ✕
                        </button>
                        <div className="relative" style={{ paddingBottom: "56.25%" }}>
                            <iframe
                                title="Trailer Player"
                                src={getYouTubeEmbedUrl(trailerUrl)}
                                className="absolute top-0 left-0 w-full h-full"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                referrerPolicy="strict-origin-when-cross-origin"
                            />
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default Home;
